import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { can } from '../../utils/permissions';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { formatCelsius } from '../../utils/thermal/legend';
import { THERMAL_ACCEPT, THERMAL_MAX_BYTES, thermalFileProblem, uploadThermal } from '../../utils/thermal/upload';
import { hereOf, thermalPath } from '../workbench/thermal/links';
import { refusalText } from './thermalText';

/*
 * The isolate dossier's Thermal strip (Photos tab; docs/FIELD_AI_VISION_SPEC.md
 * section 42), also Home's Thermal sheet: the thermal captures linked to the
 * isolate itself, its collection or intake, or one of its plates or slants --
 * only those the reader may see (GET /thermal/captures?isolate=,
 * routes/thermal_access.py).
 *
 * Each opens in the analysis workbench's thermal mode (2026-10-26; the Thermal
 * Analysis Panel over the page before): /tools/analysis?mode=thermal&
 * capture=<id>&isolate=<this isolate>&back=<this page>, so an ROI drawn there
 * is tied to this isolate unless its own picker says otherwise, and the
 * workbench's Back comes here again, the sheet or the dossier tab still open
 * (both are in the address).
 *
 * Thermal media is not a photograph (section 40): its files are private, its
 * thumbnail a 5-minute URL, and it never joins the photo gallery's rules.
 *
 * A lab writer can import a radiometric file here too, byte for byte, linked
 * to the isolate or to one of its plates or slants; it opens in the workbench
 * as soon as it is in.
 *
 * Shown to lab readers only (lab:read); for anyone else there are no thermal
 * routes, and nothing is shown.
 *
 * Given `onNavigate` (Home's sheet), "Open in the analysis workbench" opens
 * the thermal mode on the isolate itself, its captures listed first
 * (stationPath); `onNavigate(path)` is also how the captures open there,
 * else the router's own navigation.
 */
const mb = bytes => (bytes / (1024 * 1024)).toFixed(0);

/** The workbench's thermal mode on this isolate, its new ROIs tied to it; Back comes to `back`. */
export const stationPath = (specimenId, back = null) => thermalPath({ isolate: specimenId, back });

export default function ThermalStrip({ item, dossier, user, onNavigate = null }) {
  const { t } = useTranslation('thermal');
  const navigate = useNavigate();
  const location = useLocation();
  const specimenId = item?.specimen_id;
  const mayRead = can(user, 'lab:read');
  const mayImport = can(user, 'lab:observe');
  const [list, setList] = useState({ state: 'loading', items: [] });
  const [target, setTarget] = useState('specimen');
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState(null);
  const input = useRef(null);

  const load = useCallback(() => {
    if (!mayRead || !specimenId) return;
    api.get('/thermal/captures', { params: { isolate: specimenId } })
      .then(res => setList({ state: 'ready', items: Array.isArray(res.data) ? res.data : [] }))
      .catch(() => setList(l => ({ state: 'error', items: l.items })));
  }, [mayRead, specimenId]);
  useEffect(() => { load(); }, [load]);

  if (!mayRead || !specimenId) return null;

  const go = path => (onNavigate ? onNavigate(path) : navigate(path));
  /** A capture in the workbench's thermal mode, tied to this isolate; Back comes here. */
  const openCapture = id => go(thermalPath({ capture: id, isolate: specimenId, back: hereOf(location) }));

  const targets = [
    { value: 'specimen', kind: 'specimen', id: specimenId, label: t('strip.linkIsolate', { code: item.specimen_code || '' }) },
    ...(dossier?.plates || []).filter(p => p.status !== 'awaiting_creation')
      .map(p => ({ value: `plate:${p.plate_id}`, kind: 'plate', id: p.plate_id, label: t('strip.linkPlate', { code: p.plate_code }) })),
    ...(dossier?.slants || []).map(s => ({ value: `slant:${s.slant_id}`, kind: 'slant', id: s.slant_id,
      label: t('strip.linkSlant', { code: s.slant_code }) })),
  ];

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const why = thermalFileProblem(file);
    if (why) {
      setMessage({ kind: 'error', text: t(`field.problem.${why}`, { name: file.name, max: mb(THERMAL_MAX_BYTES) }) });
      return;
    }
    const link = targets.find(x => x.value === target) || targets[0];
    setImporting(true);
    setMessage(null);
    const result = await uploadThermal(file, { links: [{ kind: link.kind, id: link.id }] });
    setImporting(false);
    if (!result.ok) {
      setMessage({ kind: 'error', text: result.unreachable ? t('strip.offline') : refusalText(t, { code: result.code }, result.status) });
      return;
    }
    if (result.refusal) setMessage({ kind: 'warn', text: refusalText(t, result.refusal) });
    else setMessage({ kind: 'ok', text: t('strip.imported') });
    load();
    openCapture(result.capture.capture_id);
  };

  return (
    <section style={{ marginBlockStart: 16, display: 'flex', flexDirection: 'column', gap: 8 }}
      aria-labelledby={`thermal-strip-${specimenId}`} data-testid={`thermal-strip-${specimenId}`}>
      <h3 id={`thermal-strip-${specimenId}`} style={{ margin: 0, fontSize: 15 }}>{t('strip.title')}</h3>
      {list.state === 'loading' && <p style={hint}>{t('strip.loading')}</p>}
      {list.state === 'error' && <p style={hint} role="alert">{t('strip.failed')}</p>}
      {list.state === 'ready' && list.items.length === 0 && <p style={hint} data-testid="thermal-strip-empty">{t('strip.empty')}</p>}
      {list.items.length > 0 && (
        <ul style={strip}>
          {list.items.map((c) => {
            const thumb = resolvePhotoUrl(c.urls?.thumb);
            const d = c.derivation;
            return (
              <li key={c.capture_id}>
                <button type="button" style={card} data-testid={`thermal-strip-open-${c.capture_id}`}
                  data-status={c.status} onClick={() => openCapture(c.capture_id)}
                  title={t('strip.openTip')}>
                  {thumb ? <img src={thumb} alt="" width={120} height={90} style={img} loading="lazy" />
                    : <span aria-hidden="true" style={{ ...img, ...placeholder }}>🌡️</span>}
                  <span style={{ fontSize: 12, fontWeight: 600 }}>
                    {c.status === 'decoded' && d
                      ? <bdi dir="ltr">{formatCelsius(d.t_min_c, 1)} – {formatCelsius(d.t_max_c, 1)}</bdi>
                      : t('strip.undecoded')}
                  </span>
                  <span style={hint}>
                    <bdi dir="ltr">{(c.captured_at || c.created_at || '').slice(0, 10)}</bdi>
                    {c.analysis ? ` · ${t('strip.rois', { count: c.analysis.rois?.length || 0 })}` : ''}
                  </span>
                  {c.linked?.length > 0 && (
                    <span style={hint}>
                      {c.linked.map(l => t(`strip.kind.${l.kind}`, { code: l.code || '' })).join(', ')}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {mayImport && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
            <span style={{ fontWeight: 600 }}>{t('strip.linkTo')}</span>
            <select value={target} onChange={e => setTarget(e.target.value)} data-testid="thermal-strip-target"
              style={{ fontSize: 13, padding: '4px 6px', maxWidth: '100%' }}>
              {targets.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
            </select>
          </label>
          <button type="button" style={importBtn} disabled={importing} data-testid="thermal-strip-import"
            title={t('strip.importTip')} onClick={() => input.current?.click()}>
            {importing ? t('strip.importing') : t('strip.import')}
          </button>
          <input ref={input} type="file" accept={THERMAL_ACCEPT} style={{ display: 'none' }} tabIndex={-1}
            aria-hidden="true" data-testid="thermal-strip-input" onChange={pick} />
        </div>
      )}
      {onNavigate && (
        <button type="button" style={{ ...importBtn, alignSelf: 'flex-start' }} data-testid="thermal-strip-station"
          title={t('strip.stationTip')} onClick={() => onNavigate(stationPath(specimenId, hereOf(location)))}>
          {t('strip.station')}
        </button>
      )}
      {message && (
        <p role={message.kind === 'ok' ? 'status' : 'alert'} data-testid="thermal-strip-message" data-kind={message.kind}
          style={{ margin: 0, fontSize: 13 }}>{message.text}</p>
      )}
    </section>
  );
}

const hint = { margin: 0, fontSize: 12, color: 'var(--c-text-muted-fg)' };
const strip = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', gap: 10, overflowX: 'auto', paddingBlockEnd: 4 };
const card = { display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start', textAlign: 'start',
  background: 'var(--c-surface)', border: '1px solid var(--c-border-strong)', borderRadius: 8, padding: 6,
  cursor: 'pointer', width: 136, color: 'var(--c-text-fg)', font: 'inherit' };
const img = { width: 120, height: 90, objectFit: 'cover', borderRadius: 6, background: '#000', display: 'block' };
const placeholder = { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 };
const importBtn = { background: 'var(--c-surface)', border: '1px solid var(--c-border-strong)', borderRadius: 6,
  padding: '6px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--c-text-fg)' };
